import { groq } from 'next-sanity';
import { Article } from 'constants/blog';
import { client } from 'services/server/sanity/groq/client';

interface PostQuery {
  _id: string;
  title: string;
  publishedAt: string;
  slug: {
    current: string;
  };
  author: {
    _id: string;
    name: string;
  };
  categories: {
    title: string;
    slug: {
      current: string;
    };
  }[];
  previewDescription: string;
  mainImage: {
    asset: any;
  };
  tags: any;
}

/**
 * `includeCount: false` skips the extra `count()` round trip for callers that
 * only render a fixed-size list and never read `totalCount` (it comes back 0).
 * Every Sanity query is a billed API request, so the second one is not free.
 */
export const getAllPosts = async (
  limit: number,
  offset: number,
  categoryIds: string[],
  { includeCount = true }: { includeCount?: boolean } = {},
): Promise<{ posts: Article[]; totalCount: number }> => {
  const catIds = categoryIds;

  let categoryString = '';

  if (catIds && catIds.length === 1 && catIds[0] === 'all') {
    categoryString = '';
  } else if (catIds && catIds.length === 1 && catIds[0] !== 'all') {
    categoryString = ` && "${catIds[0]}" in categories[]->_id `;
  } else if (catIds && catIds.length > 1) {
    categoryString = ' && (';
    catIds.forEach((catId, index) => {
      if (index === 0) {
        categoryString = `${categoryString} "${catIds[index]}" in categories[]->_id `;
      } else {
        categoryString = `${categoryString} || "${catIds[index]}" in categories[]->_id `;
      }
    });
    categoryString = categoryString + ')';
  }

  let query = groq`
  *[_type == "post" ${categoryString} && !(_id in path("drafts.**")) && defined(publishedAt)]
  | order(publishedAt desc)
  [${offset}...${offset + limit}] {
      _id,
      title,
      "slug": slug,
      previewDescription,
      "author": author->{
          _id,
          name
      },
      mainImage {
        asset-> {
          _id,
          url,
          alt
        }
      },
      "categories": categories[]->{
          _id,
          title,
          "slug": {
              current
          }
      },
      "tags": tags[]->{
          title,
          "slug": {
              current
          }
      },
      publishedAt
  }
`;

  let countQuery = groq`
  count(*[_type == "post" ${categoryString} && !(_id in path("drafts.**")) && defined(publishedAt)])
  `;

  const params = {};

  const [data, totalCount] = await Promise.all([
    client.fetch<PostQuery[]>(query, params),
    includeCount ? client.fetch<number>(countQuery, params) : Promise.resolve(0),
  ]);

  const mappedData = data.map((post) => {
    return {
      _id: post._id,
      title: post?.title || '',
      slug: post?.slug?.current || '',
      previewDescription: post?.previewDescription || '',
      author: post?.author,
      mainImage: post?.mainImage?.asset?.url || '',
      categories: post?.categories,
      publishedAt: post?.publishedAt,
      previewImage: '',
      body: '',
      conclusion: '',
      tags: post?.tags,
    };
  });

  return { posts: mappedData, totalCount };
};
